import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { Submission, Review, ReviewDetail, Skill } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';

export const ReviewForm: React.FC = () => {
    const { submissionId } = useParams<{ submissionId: string }>();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [submission, setSubmission] = useState<Submission | null>(null);
    const [review, setReview] = useState<Review | null>(null);
    const [skills, setSkills] = useState<Skill[]>([]);

    // Form state
    const [overallComments, setOverallComments] = useState('');
    const [reviewDetails, setReviewDetails] = useState<ReviewDetail[]>([]);

    useEffect(() => {
        const loadData = async () => {
            apiService.getSkills().then(setSkills);

            if (submissionId) {
                // First load the submission
                const sub = await apiService.getSubmissionById(submissionId);
                setSubmission(sub || null);

                // Then try to load existing review
                const rev = await apiService.getReviewBySubmissionId(submissionId);

                if (rev) {
                    // Review exists - use review data
                    setReview(rev);
                    setOverallComments(rev.overallComments);
                    setReviewDetails(rev.details);
                } else if (sub) {
                    // No review yet - initialize with submission defaults
                    const initialDetails = sub.details.map(d => ({
                        skillId: d.skillId,
                        managerScore: d.selfScore, // Default to self score
                        managerComment: ''
                    }));
                    setReviewDetails(initialDetails);
                }
            }
        };

        loadData();
    }, [submissionId]);

    const handleDetailChange = (index: number, field: keyof ReviewDetail, value: any) => {
        const newDetails = [...reviewDetails];
        newDetails[index] = { ...newDetails[index], [field]: value };
        setReviewDetails(newDetails);
    };

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const validateForm = (isDraft: boolean = false) => {
        const newErrors: { [key: string]: string } = {};
        let isValid = true;

        reviewDetails.forEach((detail, index) => {
            // Validate score (required for both Draft and Complete)
            if (isNaN(detail.managerScore) || detail.managerScore < 1 || detail.managerScore > 5) {
                newErrors[`score_${index}`] = 'Score must be between 1 and 5';
                isValid = false;
            }

            // Comment validation only for Complete status
            if (!isDraft) {
                // Require comment if score is significantly different from self score (e.g., > 2 points diff)
                // Or just require comments for low scores
                if (detail.managerScore <= 5 && (!detail.managerComment || detail.managerComment.length < 10)) {
                    newErrors[`comment_${index}`] = 'Comments required for scores 5 or below (min 10 chars)';
                    isValid = false;
                }
            }
        });

        // Overall comments validation only for Complete status
        if (!isDraft) {
            if (!overallComments || overallComments.length < 20) {
                newErrors.overall = 'Overall comments are required (min 20 chars)';
                isValid = false;
            }
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (status: 'In Review' | 'Completed') => {
        if (!user || !submissionId) return;

        // Validate form based on status
        // For 'In Review' (Draft), we still need valid scores (1-10) for the DB
        // For 'Completed', we check everything
        const isDraft = status === 'In Review';
        if (!validateForm(isDraft)) {
            return;
        }

        // Transform review details to match backend expected property names
        const transformedDetails = reviewDetails.map(d => ({
            SkillID: d.skillId,
            ManagerScore: d.managerScore,
            ManagerComment: d.managerComment,
        }));
        const reviewData = {
            id: review?.id, // If exists, update
            submissionId,
            managerId: user.id,
            status,
            overallComments,
            details: transformedDetails,
        };

        try {
            if (review?.id) {
                // Update existing review
                await apiService.updateReview(review.id, reviewData as any);
            } else {
                // Create new review
                await apiService.createReview(reviewData as any);
            }
            navigate('/manager/dashboard');
        } catch (error) {
            console.error('Failed to save review', error);
            alert('Failed to save review. Please try again.');
        }
    };

    if (!submission) return <div>Loading...</div>;

    const getSkillName = (id: string) => skills.find(s => s.id === id)?.name || id;
    const isReadOnly = review?.status === 'Completed';

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">Review: {submission.engineerName} - {submission.period}</h1>
                {isReadOnly && (
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        Review Completed
                    </span>
                )}
            </div>

            {isReadOnly && (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-md">
                    <div className="flex">
                        <div className="ml-3">
                            <p className="text-sm text-green-700">
                                This review has been completed and is now read-only.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="space-y-6">
                {submission.details.map((detail, index) => {
                    const reviewDetail = reviewDetails.find(rd => rd.skillId === detail.skillId) || { managerScore: 0, managerComment: '' };
                    const detailIndex = reviewDetails.findIndex(rd => rd.skillId === detail.skillId);

                    return (
                        <Card key={detail.skillId}>
                            <CardHeader className="bg-gray-50 pb-4">
                                <CardTitle className="text-lg">{getSkillName(detail.skillId)}</CardTitle>
                            </CardHeader>
                            <CardContent className="pt-6 grid gap-6 md:grid-cols-2">
                                {/* Employee Section */}
                                <div className="space-y-4 border-r pr-4">
                                    <h4 className="font-semibold text-gray-700">Employee Submission</h4>
                                    <div>
                                        <span className="text-sm text-gray-500">Self Score:</span>
                                        <span className="ml-2 font-bold text-lg">{detail.selfScore}/5</span>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500 block mb-1">Achievements:</span>
                                        <p className="text-sm text-gray-800 bg-white p-3 rounded border">{detail.achievementNotes}</p>
                                    </div>

                                    {/* Certificates Display */}
                                    {detail.certificates && detail.certificates.length > 0 && (
                                        <div>
                                            <span className="text-sm text-gray-500 block mb-2">Certificates ({detail.certificates.length}):</span>
                                            <div className="space-y-2">
                                                {detail.certificates.map((cert, certIndex) => (
                                                    <div key={certIndex} className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200">
                                                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                                                            <svg className="h-4 w-4 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                            </svg>
                                                            <span className="text-xs text-gray-700 truncate">{cert.name}</span>
                                                        </div>
                                                        <a
                                                            href={cert.dataUrl}
                                                            download={cert.name}
                                                            className="text-xs text-blue-600 hover:text-blue-800 font-medium ml-2 flex-shrink-0"
                                                        >
                                                            Download
                                                        </a>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Manager Section */}
                                <div className="space-y-4 pl-2">
                                    <h4 className="font-semibold text-blue-700">Manager Review</h4>
                                    <Input
                                        label="Manager Score (1-5)"
                                        type="number"
                                        min="1"
                                        max="5"
                                        value={reviewDetail.managerScore}
                                        onChange={(e) => {
                                            const val = e.target.value === '' ? parseInt('0') : parseInt(e.target.value);
                                            // Pass 0 or NaN, let validation handle it, or we could handle it here.
                                            // Better to just pass what we parse, but maybe convert NaN to something safe or let validation catch it.
                                            // Actually, input type="number" gives us empty string if cleared.
                                            // invalid int parse returns NaN.
                                            handleDetailChange(detailIndex, 'managerScore', parseInt(e.target.value));
                                        }}
                                        error={errors[`score_${detailIndex}`]}
                                        disabled={isReadOnly}
                                    />
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
                                        <textarea
                                            className={`w-full rounded-md border p-2 text-sm ${errors[`comment_${detailIndex}`] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`}
                                            rows={3}
                                            value={reviewDetail.managerComment}
                                            onChange={(e) => handleDetailChange(detailIndex, 'managerComment', e.target.value)}
                                            placeholder="Add feedback..."
                                            disabled={isReadOnly}
                                        />
                                        {errors[`comment_${detailIndex}`] && <p className="mt-1 text-xs text-red-600">{errors[`comment_${detailIndex}`]}</p>}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}

                <Card className={errors.overall ? 'border-red-300 ring-1 ring-red-300' : ''}>
                    <CardContent className="pt-6">
                        <label className="block text-lg font-medium text-gray-900 mb-2">Overall Comments</label>
                        <textarea
                            className={`w-full rounded-md border p-3 ${errors.overall ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}`}
                            rows={4}
                            value={overallComments}
                            onChange={(e) => setOverallComments(e.target.value)}
                            placeholder="Summary of performance..."
                            disabled={isReadOnly}
                        />
                        {errors.overall && <p className="mt-1 text-sm text-red-600">{errors.overall}</p>}
                    </CardContent>
                </Card>

                <div className="flex justify-end space-x-4">
                    <Button variant="secondary" onClick={() => navigate('/manager/dashboard')}>
                        {isReadOnly ? 'Back to Dashboard' : 'Cancel'}
                    </Button>
                    {!isReadOnly && (
                        <>
                            <Button variant="outline" onClick={() => handleSubmit('In Review')}>
                                Save Progress
                            </Button>
                            <Button onClick={() => handleSubmit('Completed')}>
                                Complete Review
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
